import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useOrg, hasRole } from "@/lib/orgContext";
import { CreditCard, Building } from "iconoir-react";

const fmtIdr = (n) => "Rp" + Number(n).toLocaleString("id-ID");

function UsageBar({ label, used, allowed, testid }) {
  const pct = allowed > 0 ? Math.min(100, Math.round((used / allowed) * 100)) : 0;
  const over = used >= allowed;
  return (
    <div data-testid={testid}>
      <div className="flex items-baseline justify-between mb-1">
        <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</span>
        <span className={`font-mono text-xs ${over ? "text-intl" : "text-foreground"}`}>
          {used} / {allowed}
        </span>
      </div>
      <div className="h-1.5 w-full bg-card border border-border">
        <div
          className={`h-full ${over ? "bg-intl" : "bg-obsidian"}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

export default function WorkspaceBilling() {
  const { activeOrgId, activeOrg, activeRole } = useOrg();
  const canCheckout = hasRole(activeRole, "admin");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    if (!activeOrgId) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const r = await api.get(`/v1/organizations/${activeOrgId}/billing`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load workspace billing");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-line */ }, [activeOrgId]);

  const upgrade = async (planKey) => {
    if (!data?.ipaymu_configured) {
      toast.error("iPaymu is not configured yet");
      return;
    }
    setBusy(planKey);
    try {
      const r = await api.post(`/v1/organizations/${activeOrgId}/billing/checkout`, {
        plan_key: planKey,
        origin_url: window.location.origin,
      });
      if (r.data?.payment_url || r.data?.url) {
        window.location.href = r.data.payment_url || r.data.url;
        return;
      }
      toast.error("Checkout URL not returned");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start checkout");
    } finally {
      setBusy(null);
    }
  };

  if (!activeOrgId) return null;

  return (
    <div className="border border-border bg-card p-6 mb-8" data-testid="workspace-billing">
      <div className="flex items-center justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 border border-border flex items-center justify-center bg-background">
            <Building width={16} height={16} strokeWidth={1.6} />
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Workspace billing</div>
            <div className="font-app text-base" data-testid="workspace-billing-org">{activeOrg?.name}</div>
          </div>
        </div>
        <div className="text-right">
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Active plan</div>
          <div className="font-app text-lg capitalize" data-testid="workspace-billing-plan">
            {data?.plan_key || (loading ? "…" : "—")}
          </div>
          {data?.plan_active_until && (
            <div className="font-mono text-[10px] text-muted-foreground">
              until {String(data.plan_active_until).slice(0, 10)}
            </div>
          )}
        </div>
      </div>

      {data?.limits && data?.usage && (
        <div className="grid sm:grid-cols-3 gap-4 mt-2" data-testid="workspace-billing-usage">
          <UsageBar label="Projects" used={data.usage.projects} allowed={data.limits.projects_max} testid="usage-projects" />
          <UsageBar label="Members" used={data.usage.members} allowed={data.limits.members_max} testid="usage-members" />
          <UsageBar label="Audits this month" used={data.usage.audits_this_month} allowed={data.limits.audits_monthly} testid="usage-audits" />
        </div>
      )}

      {canCheckout && data?.available_plans?.length > 0 && (
        <div className="mt-6 pt-6 border-t border-border">
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-3">
            Upgrade this workspace
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {data.available_plans.map((p) => (
              <button
                key={p.key}
                onClick={() => upgrade(p.key)}
                disabled={busy === p.key || !data.ipaymu_configured}
                data-testid={`workspace-upgrade-${p.key}`}
                className="text-left border border-border p-4 hover:bg-background transition-colors disabled:opacity-50"
              >
                <div className="flex items-baseline justify-between">
                  <span className="font-app font-medium capitalize">{p.name}</span>
                  <span className="font-mono text-xs">{fmtIdr(p.amount_idr)} / {p.duration_days}d</span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">{p.description}</p>
                <div className="mt-3 flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-intl">
                  <CreditCard width={12} height={12} /> {busy === p.key ? "Redirecting…" : "Pay · iPaymu"}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
      {!canCheckout && (
        <div className="mt-4 font-mono text-[10px] text-muted-foreground" data-testid="workspace-billing-view-only">
          You don’t have permission to change this workspace’s plan. Ask an admin or owner.
        </div>
      )}
    </div>
  );
}
